import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { taxCodeValidationRule } from '@nxsys/utils/formFieldValidation';
import { Radio, Form } from 'antd';
import React from 'react';
import { useIntl } from 'react-intl';
import StepCard from './StepCard';
import { starterDeclarationOptions } from '@nxsys/constants/AppConstants';
import { StyledAppSelect } from '@nxsys/components/AppSelect/index.styled';

// A new starter with a P45 takes their tax code from it; without one the code
// is dictated by the starter declaration.
const NEW_WITHOUT_P45 = 'NEW_WITHOUT_P45';

// Starter declaration -> tax code / basis. Applied whenever the declaration
// changes, and whenever the fields are locked.
const TAX_BY_DECLARATION: Record<
  string,
  { taxCode: string; taxBasis: 'CUMULATIVE' | 'W1M1' }
> = {
  FIRST_JOB_SINCE_START_TAX_YEAR: {
    taxCode: '1257L',
    taxBasis: 'CUMULATIVE',
  },
  CURRENTLY_ONLY_JOB: { taxCode: '1257L', taxBasis: 'W1M1' },
  HAVE_ANOTHER_JOB: { taxCode: 'BR', taxBasis: 'CUMULATIVE' },
};

const DEFAULT_TAX = { taxCode: '1257L', taxBasis: 'CUMULATIVE' as const };

interface AddP45StarterDeclarationProps {
  stepNumber: number;
  p45StarterDeclaration?: string | null;
  taxCode?: string | null;
  taxBasis?: string | null;
  /**
   * From the preceding addP45Details step. Together these decide whether the
   * user may override the derived tax code / basis: only a new starter who
   * actually has a P45 may, because then the values come off that P45.
   */
  haveStepP45Details?: boolean;
  starterType?: string | null;
  completed?: boolean;
  onSubmitStep: (values: any) => void;
}

type AddP45StarterDeclarationValues = {
  starterDeclaration: string | null;
  taxCode: string | null;
  taxBasis: 'CUMULATIVE' | 'W1M1';
};

const AddP45StarterDeclaration: React.FC<AddP45StarterDeclarationProps> = ({
  stepNumber,
  p45StarterDeclaration,
  taxCode,
  taxBasis,
  haveStepP45Details = false,
  starterType,
  completed = false,
  onSubmitStep,
}) => {
  const [form] = Form.useForm<AddP45StarterDeclarationValues>();
  const { messages } = useIntl();

  // Editable only when the P45 Details step applies, a starter type has been
  // chosen there, and that type says the employee has a P45. Otherwise the tax
  // code / basis are derived from the declaration and locked. Locked fields are
  // still populated, so they submit and pass validation as normal.
  const isTaxEditable =
    haveStepP45Details && !!starterType && starterType !== NEW_WITHOUT_P45;

  const onFinish = (values: AddP45StarterDeclarationValues) => {
    const params = {
      p45StarterDeclaration: values?.starterDeclaration || null,
      taxCode: values?.taxCode || null,
      taxBasis: values?.taxBasis || null,
    };
    onSubmitStep(params);
  };

  return (
    <StepCard
      title={messages['prospect.onboarding.addP45StarterDeclaration'] as string}
      stepNumber={stepNumber}
      completed={completed}
      actions={[
        {
          variant: 'outlined',
          name: messages['common.save'] as string,
          onClick: () => form.submit(),
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onValuesChange={(changedValues: any) => {
          // Derive taxCode / taxBasis from the chosen starter declaration. This
          // runs even when the fields are editable — picking a declaration
          // re-seeds them, and the user can then adjust if they have a P45.
          if (
            'starterDeclaration' in changedValues &&
            changedValues.starterDeclaration
          ) {
            form.setFieldsValue(
              TAX_BY_DECLARATION[changedValues.starterDeclaration] ??
                DEFAULT_TAX,
            );
          }
        }}
        initialValues={{
          starterDeclaration: p45StarterDeclaration ?? undefined,
          taxCode: taxCode ?? undefined,
          taxBasis: (taxBasis as AddP45StarterDeclarationValues['taxBasis']) || 'CUMULATIVE',
        }}
        wrapperCol={{ span: 24 }}
        layout="vertical"
      >
        <Form.Item
          label={messages['prospect.onboarding.starterDeclaration'] as string}
          name="starterDeclaration"
          rules={[
            {
              required: true,
              message: messages['form.pleaseSelect'] as string,
            },
          ]}
        >
          <StyledAppSelect options={starterDeclarationOptions} placeholder={messages['prospect.onboarding.starterDeclaration.placeholder'] as string} />
        </Form.Item>

        <Form.Item
          label={messages['prospect.onboarding.taxCode'] as string}
          name="taxCode"
          rules={[
            {
              required: true,
              message: messages['form.pleaseEnter'] as string,
            },
            taxCodeValidationRule(messages as Record<string, string>),
          ]}
        >
          <AppInput
            upperCaseAll
            disabled={!isTaxEditable}
            placeholder={`${messages['form.enter'] as string} ${
              messages['prospect.onboarding.taxCode'] as string
            }`}
          />
        </Form.Item>

        <Form.Item
          label={messages['prospect.onboarding.taxBasis'] as string}
          name="taxBasis"
          rules={[
            {
              required: true,
              message: messages['form.pleaseSelect'] as string,
            },
          ]}
        >
          <Radio.Group disabled={!isTaxEditable}>
            <Radio value="CUMULATIVE">
              {messages['prospect.onboarding.taxBasis.cumulative'] as string}
            </Radio>
            <Radio value="W1M1">
              {messages['prospect.onboarding.taxBasis.w1m1'] as string}
            </Radio>
          </Radio.Group>
        </Form.Item>
      </AppFormContainer>
    </StepCard>
  );
};

export default AddP45StarterDeclaration;
